import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Check } from "lucide-react";

/**
 * Delivering animation: a chain of small letters, hearts flying between them,
 * ending with a "Delivered" confirmation.
 * Props:
 *   onComplete: () => void   - fires after the ceremony ends.
 */
export default function DeliveringAnimation({ onComplete }) {
  const LETTER_COUNT = 5;
  const [progress, setProgress] = useState(0); // 0..LETTER_COUNT
  const [showDelivered, setShowDelivered] = useState(false);

  useEffect(() => {
    const step = 620;
    const timers = [];
    for (let i = 1; i <= LETTER_COUNT; i++) {
      timers.push(setTimeout(() => setProgress(i), i * step));
    }
    timers.push(setTimeout(() => setShowDelivered(true), (LETTER_COUNT + 1) * step));
    timers.push(setTimeout(() => onComplete && onComplete(), (LETTER_COUNT + 3) * step));
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 cream-bg flex flex-col items-center justify-center px-6"
    >
      <p className="uppercase tracking-[0.4em] text-xs text-[#B8935A]">Delivering your heart</p>
      <h2 className="mt-4 font-serif-elegant text-4xl md:text-5xl text-[#1C1917] text-center max-w-2xl">
        {showDelivered ? "Delivered." : "Passing from hand to hand…"}
      </h2>

      {/* Chain of letters */}
      <div className="mt-14 flex items-center justify-center gap-4 md:gap-8 flex-wrap max-w-4xl">
        {Array.from({ length: LETTER_COUNT }).map((_, i) => {
          const active = progress >= i + 1;
          return (
            <div key={i} className="relative flex items-center">
              <motion.div
                initial={{ y: 12, opacity: 0.3, scale: 0.9 }}
                animate={{
                  y: active ? -6 : 12,
                  opacity: active ? 1 : 0.35,
                  scale: active ? 1 : 0.9,
                  rotate: active ? [0, -3, 3, 0] : 0,
                }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <MiniLetter active={active} />
              </motion.div>

              {i < LETTER_COUNT - 1 && (
                <div className="relative w-10 md:w-14 h-8 flex items-center justify-center">
                  {/* connecting dotted line */}
                  <div className="absolute left-0 right-0 top-1/2 h-[1px] border-t border-dashed border-[#B8935A]/50" />
                  <AnimatePresence>
                    {progress > i && (
                      <motion.div
                        initial={{ x: -30, opacity: 0, scale: 0.4 }}
                        animate={{ x: 30, opacity: [0, 1, 1, 0], scale: [0.4, 1, 1, 0.8] }}
                        transition={{ duration: 0.55, ease: "easeOut" }}
                        className="absolute"
                      >
                        <Heart size={14} className="text-[#991B1B]" fill="#991B1B" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* floating hearts backdrop */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 14 }).map((_, i) => (
          <motion.div
            key={i}
            initial={{ y: "100vh", x: `${(i / 14) * 100}%`, opacity: 0 }}
            animate={{ y: "-10vh", opacity: [0, 0.6, 0] }}
            transition={{ duration: 6 + Math.random() * 3, delay: i * 0.4, repeat: Infinity }}
            className="absolute"
          >
            <Heart size={10 + Math.random() * 8} className="text-[#991B1B]/40" fill="#991B1B" />
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showDelivered && (
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
            className="mt-14 flex items-center gap-3 px-6 py-3 rounded-full bg-[#991B1B] text-white shadow-xl shadow-[#991B1B]/30"
          >
            <span className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center">
              <Check size={14} />
            </span>
            <span className="text-sm tracking-wide">Delivered with love</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function MiniLetter({ active }) {
  return (
    <div className="relative" style={{ width: 78, height: 56 }}>
      <div
        className="absolute inset-0 rounded-[4px]"
        style={{
          background: active
            ? "linear-gradient(180deg, #F5E7C9 0%, #E7D3A6 100%)"
            : "linear-gradient(180deg, #EFE6D1 0%, #DDCFA9 100%)",
          boxShadow: active ? "0 8px 18px -6px rgba(120,80,40,0.35)" : "0 3px 8px rgba(0,0,0,0.06)",
        }}
      />
      <svg className="absolute inset-0" viewBox="0 0 78 56">
        <polygon points="0,0 78,0 39,32" fill={active ? "#E4D3A6" : "#D5C29A"} stroke="rgba(60,40,15,0.25)" strokeWidth="0.6" />
      </svg>
      {active && (
        <div
          className="absolute rounded-full"
          style={{
            width: 14, height: 14, left: 32, top: 22,
            background: "radial-gradient(circle at 35% 30%, #d64545 0%, #7f1d1d 70%, #4a0f0f 100%)",
            boxShadow: "0 2px 4px rgba(120,20,20,0.6)",
          }}
        />
      )}
    </div>
  );
}
